import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function InvitationPage() {
  const { secretCode: urlSecretCode } = useParams();
  const [secretCode, setSecretCode] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [enhancedAttention, setEnhancedAttention] = useState(false);

  const locationUrl = "https://maps.google.com/?q=Moonlight+Hotel+Marawila";

  useEffect(() => {
    if (urlSecretCode) {
      validateCode(urlSecretCode);
    }
  }, [urlSecretCode]);

  useEffect(() => {
    if (invitation && !invitation.hasResponded) {
      const timer = setTimeout(() => {
        setEnhancedAttention(true);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [invitation]);

  const validateCode = async (codeToValidate) => {
    setIsLoading(true);
    try {
      const result = await axios.post(`${API_URL}/invitation/validate`, {
        secretCode: codeToValidate
      });
      setInvitation(result.data);
      if (result.data.hasResponded) {
        setResponse(result.data.isAttending ? 'accepted' : 'declined');
      }
    } catch (error) {
      alert('Invalid secret code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    validateCode(secretCode);
  };

  const submitResponse = async (isAttending) => {
    try {
      await axios.post(`${API_URL}/invitation/respond`, {
        secretCode: urlSecretCode || secretCode,
        name: invitation.name,
        isAttending
      });
      setInvitation(prev => ({
        ...prev,
        hasResponded: true,
        isAttending: isAttending
      }));
      setResponse(isAttending ? 'accepted' : 'declined');
    } catch (error) {
      alert('Error submitting response');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (response) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary-50 to-white relative overflow-hidden">
        <div 
          className="max-w-2xl w-full h-auto min-h-[85vh] flex flex-col items-center justify-center overflow-hidden bg-white rounded-2xl shadow-lg p-3 sm:p-4 md:p-5 text-center relative z-10 border border-primary-200 mx-auto"
          style={{
            backgroundImage: 'url("/background.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-white/30 rounded-2xl"></div>
          
          <div className="relative w-full max-w-md mx-auto flex flex-col justify-between h-full py-6 sm:py-8">
            <div>
              <div className="mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl text-primary-900 font-dancing">Together with their families</h2>
              </div>

              <div className="mb-3 sm:mb-4">
                <h1 className="text-3xl sm:text-4xl text-[#8B4513] mb-1 font-dancing">
                  <span className="sm:hidden">Visitha</span>
                  <span className="hidden sm:inline">Visitha Wijesinghe</span>
                </h1>
                <div className="text-2xl sm:text-3xl text-primary-500 font-dancing">&</div>
                <h1 className="text-3xl sm:text-4xl text-[#8B4513] mt-1 font-dancing">
                  <span className="sm:hidden">Muthu</span>
                  <span className="hidden sm:inline">Dineshika Appuhamy</span>
                </h1>
              </div>

              <div className="mb-3 sm:mb-4 text-gray-700">
                <p className="text-md sm:text-lg mb-0">request the honor of your presence</p>
                <p className="text-md sm:text-lg">as they unite in marriage</p>
              </div>

              <div className="mb-3 sm:mb-4">
                <h2 className="text-2xl sm:text-3xl text-[#8B4514] font-dancing">{invitation.name}</h2>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl text-[#8B4513] font-dancing">Saturday, 10th May 2025</h2>
                <p className="mt-3 sm:mt-3 text-md sm:text-lg text-gray-700">
                  from 7:00 PM to 12:00 AM
                  <span className="block -mt-1 font-dancing">(Poruwa Ceremony at 5:40 PM)</span>
                </p>
                <div className="mt-3 sm:mt-3">
                  <h3 className="text-lg sm:text-xl text-[#8B4513] font-dancing mb-0">The Royal Ballroom</h3>
                  <p className="text-md sm:text-lg text-gray-700">Moonlight Hotel, Marawila</p>
                </div>
              </div>
            </div>

            <div>
              <div 
                onClick={() => window.open(locationUrl, '_blank')}
                className="mt-3 sm:mt-4 text-primary-600 text-xs sm:text-sm cursor-pointer hover:text-primary-700 transition-colors flex items-center justify-center gap-1 sm:gap-2"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Tap to view the location</span>
              </div>

              <div className={`mt-3 sm:mt-4 p-2 sm:p-3 rounded-lg ${
                response === 'accepted' 
                  ? 'bg-primary-50' 
                  : 'bg-red-50'
              }`}>
                <h2 className={`text-xl sm:text-2xl font-dancing mb-0 ${
                  response === 'accepted'
                    ? 'text-primary-600'
                    : 'text-red-600'
                }`}>Thank You!</h2>
                <p className={
                  response === 'accepted'
                    ? 'text-primary-700 text-sm sm:text-base'
                    : 'text-red-700 text-sm sm:text-base'
                }>
                  You have {response} the invitation. 
                  {response === 'accepted' 
                    ? " We're excited to celebrate with you!" 
                    : " We'll miss you at our celebration."}
                </p>
                <button
                  onClick={() => submitResponse(!invitation.isAttending)}
                  className={`mt-1 sm:mt-2 px-4 sm:px-6 py-1 sm:py-2 text-white rounded-full transition-colors text-xs sm:text-sm ${
                    response === 'accepted'
                      ? 'bg-primary-600 hover:bg-primary-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  Change to {invitation.isAttending ? 'Not Attending' : 'Attending'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary-50 to-white relative overflow-hidden">
        <div 
          className="max-w-2xl w-full h-auto min-h-[85vh] flex flex-col items-center justify-center overflow-hidden bg-white rounded-2xl shadow-lg p-3 sm:p-4 md:p-5 text-center relative z-10 border border-primary-200 mx-auto"
          style={{
            backgroundImage: 'url("/background.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div className="absolute inset-0 bg-white/30 rounded-2xl"></div>
          
          <div className="relative w-full max-w-md mx-auto flex flex-col justify-between h-full py-6 sm:py-8">
            <div>
              <div className="mb-3 sm:mb-4">
                <h2 className="text-lg sm:text-xl text-primary-900 font-dancing">Together with their families</h2>
              </div>

              <div className="mb-3 sm:mb-4">
                <h1 className="text-3xl sm:text-4xl text-[#8B4513] mb-1 font-dancing">
                  <span className="sm:hidden">Visitha</span>
                  <span className="hidden sm:inline">Visitha Wijesinghe</span>
                </h1>
                <div className="text-2xl sm:text-3xl text-primary-500 font-dancing">&</div>
                <h1 className="text-3xl sm:text-4xl text-[#8B4513] mt-1 font-dancing">
                  <span className="sm:hidden">Muthu</span>
                  <span className="hidden sm:inline">Dineshika Appuhamy</span>
                </h1>
              </div>

              <div className="mb-3 sm:mb-4 text-gray-700">
                <p className="text-md sm:text-lg mb-0">request the honor of your presence</p>
                <p className="text-md sm:text-lg">as they unite in marriage</p>
              </div>

              <div className="mb-3 sm:mb-4">
                <h2 className="text-2xl sm:text-3xl text-[#8B4514] font-dancing">{invitation.name}</h2>
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl text-[#8B4513] font-dancing">Saturday, 10th May 2025</h2>
                <p className="mt-3 sm:mt-3 text-md sm:text-lg text-gray-700">
                  from 7:00 PM to 12:00 AM
                  <span className="block -mt-1 font-dancing">(Poruwa Ceremony at 5:40 PM)</span>
                </p>
                <div className="mt-3 sm:mt-3">
                  <h3 className="text-lg sm:text-xl text-[#8B4513] font-dancing mb-0">The Royal Ballroom</h3>
                  <p className="text-md sm:text-lg text-gray-700">Moonlight Hotel, Marawila</p>
                </div>
              </div>
            </div>

            <div>
              <div 
                onClick={() => window.open(locationUrl, '_blank')}
                className="mt-3 sm:mt-4 text-primary-600 text-xs sm:text-sm cursor-pointer hover:text-primary-700 transition-colors flex items-center justify-center gap-1 sm:gap-2"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Tap to view the location</span>
              </div>

              {!invitation.hasResponded && (
                <div className="mt-3 sm:mt-4 flex justify-center gap-3 sm:gap-4">
                  <button
                    onClick={() => submitResponse(true)}
                    className={`px-3 sm:px-6 py-1 sm:py-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors text-sm sm:text-base blink-attention ${enhancedAttention ? 'shadow-lg' : ''}`}
                  >
                    <span className="hidden sm:inline">Joyfully </span>Accept
                  </button>
                  <button
                    onClick={() => submitResponse(false)}
                    className="px-3 sm:px-6 py-1 sm:py-2 bg-primary-100 text-primary-700 rounded-full hover:bg-primary-200 transition-colors text-sm sm:text-base"
                  >
                    <span className="hidden sm:inline">Regretfully </span>Decline
                  </button>
                </div>
              )}

              {invitation.hasResponded && (
                <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-primary-50 rounded-lg">
                  <p className="text-primary-700 text-sm mb-1 sm:mb-2">
                    You have previously {invitation.isAttending ? 'accepted' : 'declined'} this invitation.
                  </p>
                  <button
                    onClick={() => submitResponse(!invitation.isAttending)}
                    className="px-3 sm:px-6 py-1 sm:py-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors text-xs sm:text-sm"
                  >
                    Change to {invitation.isAttending ? 'Not Attending' : 'Attending'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (urlSecretCode) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary-50 to-white relative overflow-hidden">
      <div className="max-w-md w-full h-auto bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center relative z-10 border border-primary-200 mx-auto"
        style={{
          backgroundImage: 'url("/background.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="absolute inset-0 bg-white/30 rounded-2xl"></div>
        
        <div className="relative transform origin-center w-full mx-auto" style={{ transform: 'scale(0.98)' }}>
        <h2 className="text-2xl font-dancing text-primary-600 mb-6">Our Wedding Invitation</h2>
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl text-[#8B4513] mb-1 font-dancing">
              <span className="sm:hidden">Visitha</span>
              <span className="hidden sm:inline">Visitha Wijesinghe</span>
            </h1>
            <div className="text-2xl sm:text-3xl text-primary-500 font-dancing">&</div>
            <h1 className="text-4xl sm:text-5xl text-[#8B4513] mt-1 font-dancing">
              <span className="sm:hidden">Muthu</span>
              <span className="hidden sm:inline">Dineshika Appuhamy</span>
            </h1>
          </div>
          
         
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="mb-6">
              <input
                type="text"
                placeholder="Enter your secret code"
                value={secretCode}
                onChange={(e) => setSecretCode(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none transition-all bg-white/75"
              />
            </div>
            <button
              type="submit"
              className="w-full px-6 py-3 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors"
            >
              View Invitation
            </button>
          </form>
          
          <div className="mt-6 text-primary-700 text-sm">
            <p>Please enter the code provided in your invitation</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvitationPage; 